/**
 * Configuration Sentry pour B8ild
 * Error monitoring et performance tracing
 */
import * as Sentry from "@sentry/react";

/**
 * Initialise Sentry avec la configuration optimale pour B8ild
 * - Actif uniquement en production
 * - Tracing des performances à 10%
 * - Filtrage des erreurs de développement
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Ne pas initialiser si pas de DSN configuré
  if (!dsn) {
    console.warn('[Sentry] DSN non configuré, monitoring désactivé');
    return;
  }

  Sentry.init({
    dsn,

    // Environnement automatique (development/production)
    environment: import.meta.env.MODE,

    // Actif uniquement en production
    enabled: import.meta.env.PROD,

    // Intégrations
    integrations: [
      // Tracing des performances (navigation, requêtes)
      Sentry.browserTracingIntegration(),
      // Replay des sessions en cas d'erreur
      Sentry.replayIntegration({
        maskAllText: true,      // Masquer textes sensibles
        blockAllMedia: true,    // Masquer images/vidéos
      }),
    ],

    // 10% des transactions pour le tracing (optimisation coûts)
    tracesSampleRate: 0.1,

    // Session Replay : 10% des sessions normales, 100% en cas d'erreur
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Envoie les infos utilisateur par défaut (IP, etc.)
    sendDefaultPii: true,

    // Filtre avant envoi
    beforeSend(event) {
      // Ne jamais envoyer en développement
      if (import.meta.env.DEV) {
        return null;
      }

      // Filtrer les erreurs de réseau courantes (non critiques)
      const message = event.exception?.values?.[0]?.value || '';
      const ignoredErrors = [
        'ResizeObserver loop',
        'Network request failed',
        'Load failed',
        'Failed to fetch',
      ];

      if (ignoredErrors.some(err => message.includes(err))) {
        return null;
      }

      return event;
    },
  });
}

/**
 * ErrorBoundary Sentry pré-configuré
 * À utiliser pour wrapper l'application React
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Capture manuelle d'une erreur
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, { extra: context });
  } else {
    console.error('[Sentry Dev]', error, context);
  }
}

/**
 * Capture manuelle d'un message
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[Sentry Dev - ${level}]`, message);
  }
}

/**
 * Définit l'utilisateur courant pour Sentry
 */
export function setUser(user: { id: string; email?: string; username?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * Ajoute du contexte additionnel
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  Sentry.setContext(name, context);
}

export { Sentry };
